/// <reference path="../global.d.ts" />
import { Box, Input, Text } from '@mantine/core';
import { en } from 'intl-tel-input/i18n';
import intlTelInput from 'intl-tel-input/intlTelInputWithUtils';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
	type FC,
	memo,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState
} from 'react';
import 'intl-tel-input/styles';
import { cn } from '../utils/cn';
import styles from './PhoneInput.module.css';
import type { IntlTelInputInstance, PhoneInputProps } from './types';
import { syncCountryNames, syncIntlTelInputUi } from './utils';

const getInitialPhoneValidity = (phoneValue: string | null | undefined) => {
	const trimmedPhoneValue = phoneValue?.trim();

	if (!trimmedPhoneValue) {
		return undefined;
	}

	return isValidPhoneNumber(trimmedPhoneValue);
};

function assertIntlTelInputInstance(
	instance: unknown
): asserts instance is IntlTelInputInstance {
	if (!instance || typeof instance !== 'object') {
		throw new Error('Unexpected intl-tel-input instance shape');
	}

	const candidate = instance as Partial<IntlTelInputInstance>;

	if (
		!Array.isArray(candidate.countries) ||
		!candidate.options ||
		!candidate.ui ||
		typeof candidate.getSelectedCountryData !== 'function' ||
		typeof candidate.getNumber !== 'function' ||
		typeof candidate.getValidationError !== 'function' ||
		typeof candidate.isValidNumber !== 'function' ||
		typeof candidate.destroy !== 'function' ||
		typeof candidate._setCountry !== 'function'
	) {
		throw new Error('Unexpected intl-tel-input instance shape');
	}
}

const PhoneInputImpl: FC<PhoneInputProps> = ({
	className,
	name,
	value,
	initialValue,
	label,
	description,
	inputProps,
	onBlur,
	onChange,
	onChangeValidity,
	error,
	initialCountry = 'us',
	i18n,
	dropdownContainer,
	...rest
}) => {
	// Preserve the saved phone validity on remount so a valid prefilled number
	// does not flash with the invalid border before intl-tel-input emits updates.
	const [isValid, setIsValid] = useState<boolean | undefined>(() =>
		getInitialPhoneValidity(initialValue ?? value)
	);
	const id = useId();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const intlTelInputRef = useRef<IntlTelInputInstance | null>(null);
	const isMountedRef = useRef(false);
	const initialCountryRef = useRef(initialCountry);
	/**
	 * `intl-tel-input` fires `onChangeNumber` on mount (and on country
	 * changes) with the normalized value, even when the user hasn't typed
	 * anything. Without this guard every consumer-side state setter behind
	 * `onChange` runs at least once per mount, producing an extra render
	 * pass and — for forms inside Drawer/Modal trees — a visible cascade.
	 *
	 * We mirror the previous local PhoneInput behavior: only forward
	 * `onChange` when the value actually changes.
	 */
	const lastReportedValueRef = useRef(initialValue ?? value ?? '');

	useEffect(() => {
		if (!i18n) {
			return;
		}

		const intlTelInputInstance = intlTelInputRef.current;

		if (!intlTelInputInstance) {
			return;
		}

		intlTelInputInstance.options.i18n = {
			...en,
			...i18n
		};
		syncCountryNames(intlTelInputInstance);
		syncIntlTelInputUi(intlTelInputInstance);
	}, [i18n]);

	const handleChangeValidity = useCallback(
		(nextValid: boolean) => {
			onChangeValidity?.(nextValid);
			setIsValid(nextValid);
		},
		[onChangeValidity]
	);

	const handleChangeNumber = useCallback(
		(nextValue: string) => {
			if (lastReportedValueRef.current === nextValue) {
				return;
			}

			lastReportedValueRef.current = nextValue;
			onChange(nextValue);
		},
		[onChange]
	);

	const handleIntlTelInputUpdate = useCallback(() => {
		const input = inputRef.current;
		const intlTelInputInstance = intlTelInputRef.current;

		if (!isMountedRef.current || !input || !intlTelInputInstance) {
			return;
		}

		const rawValue = input.value;
		if (!rawValue.trim()) {
			handleChangeNumber('');
			return;
		}

		const fallbackValue = rawValue.replace(/[^\d+]/g, '');
		const isValidNumber = intlTelInputInstance.isValidNumber() === true;
		const normalizedValue = intlTelInputInstance.getNumber();
		const nextValue = isValidNumber ? normalizedValue : fallbackValue;

		handleChangeNumber(nextValue);
		handleChangeValidity(isValidNumber);
	}, [handleChangeNumber, handleChangeValidity]);

	useEffect(() => {
		const input = inputRef.current;

		if (!input) {
			return;
		}

		isMountedRef.current = true;
		const intlTelInputInstance: unknown = intlTelInput(input, {
			strictMode: true,
			initialCountry: initialCountryRef.current,
			...(i18n ? { i18n } : {}),
			...(dropdownContainer ? { dropdownContainer } : {})
		});
		assertIntlTelInputInstance(intlTelInputInstance);
		intlTelInputRef.current = intlTelInputInstance;

		input.addEventListener('countrychange', handleIntlTelInputUpdate);

		void intlTelInputInstance.promise.then(() => {
			if (intlTelInputRef.current !== intlTelInputInstance) {
				return;
			}
			handleIntlTelInputUpdate();
		});

		return () => {
			isMountedRef.current = false;
			input.removeEventListener('countrychange', handleIntlTelInputUpdate);
			intlTelInputRef.current = null;
			intlTelInputInstance.destroy();
		};
	}, [dropdownContainer, handleIntlTelInputUpdate, i18n]);

	const { onBlur: userOnBlur, ...restInputProps } = inputProps ?? {};

	return (
		<Box
			className={className}
			{...rest}
		>
			{label && (
				<label
					className="text-sm"
					htmlFor={id}
				>
					{label}
					<Text
						c="red"
						component="span"
						fz={12}
					>
						&#42;
					</Text>
				</label>
			)}
			{description && (
				<Input.Description style={{ fontSize: 10 }}>
					{description}
				</Input.Description>
			)}
			<Box className={styles.container}>
				<Box
					component="input"
					id={id}
					name={name}
					ref={inputRef}
					type="tel"
					{...restInputProps}
					className={cn(styles.input, {
						[styles.invalid]: value?.trim() && !isValid
					})}
					defaultValue={initialValue}
					onBlur={(event) => {
						userOnBlur?.(event);
						onBlur?.();
					}}
					onInput={handleIntlTelInputUpdate}
				/>
			</Box>
			{error ? (
				<Text
					c="red"
					fz={10}
				>
					{error}
				</Text>
			) : undefined}
		</Box>
	);
};

export const PhoneInput = memo(PhoneInputImpl);
